import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { useAuthStore } from './stores/authStore';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import AdminLayout from './components/admin/AdminLayout';

// Pages
import HomePage from './pages/HomePage';
import CataloguePublicPage from './pages/publiques/CataloguePublicPage';
import StagePublicPage from './pages/publiques/StagePublicPage';
import ConfidentialitePage from './pages/publiques/ConfidentialitePage';
import ListePubliquePage from './pages/publiques/ListePubliquePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/profile/ProfilePage';
import CoordonneesPage from './pages/profile/CoordonneesPage';
import KidsPage from './pages/profile/KidsPage';
import NewKidPage from './pages/profile/NewKidPage';
import KidDetailsPage from './pages/profile/KidDetailsPage';
import VerifierKidPage from './pages/profile/VerifierKidPage';
import InvoicesPage from './pages/profile/InvoicesPage';
import AttestationsPage from './pages/profile/AttestationsPage';
import AuthorizedPersonsPage from './pages/authorized-persons/AuthorizedPersonsPage';
import NewAuthorizedPersonPage from './pages/authorized-persons/NewAuthorizedPersonPage';
import EditAuthorizedPersonPage from './pages/authorized-persons/EditAuthorizedPersonPage';
import ActivitiesPage from './pages/activities/ActivitiesPage';
import CartPage from './pages/checkout/CartPage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import OrderConfirmationPage from './pages/checkout/OrderConfirmationPage';
import InvoiceConfirmationPage from './pages/checkout/InvoiceConfirmationPage';
import RegistrationsPage from './pages/registrations/RegistrationsPage';
import NotFoundPage from './pages/NotFoundPage';

// Espace staff "Gestion CDV" (ex-webapp EclosionASBL/anim)
import StaffApp from './staff/App';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminCentresPage from './pages/admin/centres/AdminCentresPage';
import AdminCentreFichePage from './pages/admin/centres/AdminCentreFichePage';
import AdminStagesPage from './pages/admin/AdminStagesPage';
import AdminProgrammationPage from './pages/admin/AdminProgrammationPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserPage from './pages/admin/AdminUserPage';
import AdminUserFinancesPage from './pages/admin/AdminUserFinancesPage';
import AdminUserEditionPage from './pages/admin/AdminUserEditionPage';
import AdminFacturesPage from './pages/admin/AdminFacturesPage';
import AdminCodaReconciliationPage from './pages/admin/AdminCodaReconciliationPage';
import AdminRepriseOuverturePage from './pages/admin/AdminRepriseOuverturePage';
import AdminTarifConditionsPage from './pages/admin/AdminTarifConditionsPage';
import AdminSchoolsPage from './pages/admin/AdminSchoolsPage';
import AdminWaitingListPage from './pages/admin/AdminWaitingListPage';
import AdminInclusionRequestsPage from './pages/admin/AdminInclusionRequestsPage';
import AdminCancellationRequestsPage from './pages/admin/AdminCancellationRequestsPage';
import AdminNewsletterPage from './pages/admin/AdminNewsletterPage';
import AdminReglagesSitePage from './pages/admin/AdminReglagesSitePage';
import AdminRegistrationsByGroupPage from './pages/admin/AdminRegistrationsByGroupPage';
import AdminFinancialOverviewPage from './pages/admin/AdminFinancialOverviewPage';
import AdminInscriptionsPage from './pages/admin/AdminInscriptionsPage';
import AdminGiftCodesPage from './pages/admin/AdminGiftCodesPage';
import AdminAttestationsPage from './pages/admin/AdminAttestationsPage';
import AdminUtilisateursPage from './pages/admin/AdminUtilisateursPage';
import AdminUtilisateursDonneesPage from './pages/admin/AdminUtilisateursDonneesPage';
import ComptabiliteAccueilPage from './pages/admin/comptabilite/ComptabiliteAccueilPage';
import JournauxPage from './pages/admin/comptabilite/JournauxPage';
import EcrituresPage from './pages/admin/comptabilite/EcrituresPage';
import EcriturePage from './pages/admin/comptabilite/EcriturePage';
import CompteDAttentePage from './pages/admin/comptabilite/CompteDAttentePage';
import PlanificationAccueilPage from './pages/admin/planification/PlanificationAccueilPage';
import JourneesPedagogiquesPage from './pages/jpa/JourneesPedagogiquesPage';
import AdminJourneesPage from './pages/admin/jpa/AdminJourneesPage';
import AdminJourneeFormPage from './pages/admin/jpa/AdminJourneeFormPage';
import AdminGarderieFacturationPage from './pages/admin/garderie/AdminGarderieFacturationPage';
import AdminJourneeListesPage from './pages/admin/jpa/AdminJourneeListesPage';
import AdminJourneeGroupesPage from './pages/admin/jpa/AdminJourneeGroupesPage';
import PresencesLienPage from './pages/presences/PresencesLienPage';
import PointageLienPage from './pages/garderie/PointageLienPage';
import EspaceLayout from './components/admin/EspaceLayout';
import Redirection from './components/navigation/Redirection';
import RedirectionParRole from './components/navigation/RedirectionParRole';
import { CHEMINS, REDIRECTIONS } from './lib/navigation/chemins';
import { COMPTABILITE, PLANIFICATION } from './lib/navigation/espaces';

// Components
import LoadingScreen from './components/common/LoadingScreen';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/admin/AdminRoute';
import StaffRoute from './components/auth/StaffRoute';
import { landingPathForRole } from './lib/roleRouting';

/** L'ancienne adresse de modification renvoie vers la fiche, qui l'assure. */
const RedirectionFicheEnfant = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/kids/${id}` : '/kids'} replace />;
};

function App() {
  const { user, profile, profileLoaded, setUser, fetchProfile } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Amorcage de la session, une seule fois au montage.
  //
  // Cet effet dependait auparavant de `location`, donc il relancait getSession()
  // et re-souscrivait a onAuthStateChange a chaque changement de route.
  //
  // Surtout : il ne chargeait que l'utilisateur, jamais son profil. Comme le
  // role vit dans la table `users` et non dans app_metadata, les gardes de
  // route voyaient un role inconnu apres chaque rafraichissement et renvoyaient
  // vers l'espace parent. D'ou l'appel a fetchProfile() ici.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        void fetchProfile();
      }
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        void fetchProfile();
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [setUser, fetchProfile]);

  // Un utilisateur deja connecte qui atterrit sur `/` est envoye vers son
  // espace. On attend `profileLoaded` : sans cette garde, un coordinateur ou un
  // administrateur serait dirige vers l'espace parent simplement parce que son
  // role n'est pas encore connu -- et rien ne le ramenerait ensuite.
  useEffect(() => {
    if (!user || location.pathname !== '/' || !profileLoaded) {
      return;
    }
    const role = (user.app_metadata?.role as string) || profile?.role;
    navigate(landingPathForRole(role), { replace: true });
  }, [user, profile?.role, profileLoaded, location.pathname, navigate]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Public & parent routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />

        {/*
          Le catalogue public. HORS `<ProtectedRoute />`, et c'est tout l'objet :
          jusqu'au 11 septembre 2026, `/activities` etait a l'interieur, donc
          Google, Bing et ChatGPT ne voyaient qu'une seule page de tout le site.

          `/activities` reste ou il est : c'est l'ecran d'inscription d'un parent
          connecte, avec son panier et ses tarifs par enfant. Ces deux pages-ci
          ne montrent que ce qui est publiable, depuis `v_stages_publics`.
        */}
        <Route path="/stages" element={<CataloguePublicPage />} />
        {/*
          Les pages de commune et de periode AVANT `/stages/:slug` : React
          Router prend la premiere route qui correspond, et `commune` serait
          sinon lu comme le slug d'un stage.
        */}
        <Route
          path="/stages/commune/:slug"
          element={<ListePubliquePage genre="commune" />}
        />
        <Route
          path="/stages/vacances/:slug"
          element={<ListePubliquePage genre="vacances" />}
        />
        <Route path="/stages/:slug" element={<StagePublicPage />} />

        {/*
          La politique de confidentialite, publique et hors connexion : une
          famille doit pouvoir la lire AVANT de creer un compte, et le
          formulaire du droit a l'image y renvoie au moment ou il demande un
          consentement.
        */}
        <Route path="/confidentialite" element={<ConfidentialitePage />} />

        {/*
          Les journees pedagogiques, hors `<ProtectedRoute />` : c'est l'adresse
          que l'ecole envoie aux familles. Sans compte, la page dit comment en
          creer un ; connecte, elle inscrit.
        */}
        <Route path="/journees-pedagogiques" element={<JourneesPedagogiquesPage />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route 
            path="/login" 
            element={user ? <Navigate to="/dashboard\" replace /> : <LoginPage />} 
          />
          <Route 
            path="/register" 
            element={user ? <Navigate to="/dashboard\" replace /> : <RegisterPage />} 
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Retour de Google : hors de la garde `user ?` de /login, qui
              enverrait tout le monde vers /dashboard sans regarder le role. */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
        </Route>

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          {/* Le premier écran d'un nouveau parent : ses coordonnées, avant la fiche enfant */}
          <Route path="/bienvenue" element={<CoordonneesPage />} />
          <Route path="/profile/invoices" element={<InvoicesPage />} />
          <Route path="/profile/attestations" element={<AttestationsPage />} />
          <Route path="/kids" element={<KidsPage />} />
          <Route path="/kids/new" element={<NewKidPage />} />
          {/*
            `/kids/edit/:id` a existé et n'enregistrait rien : la page envoyait
            les colonnes de kid_health, kid_allergies, kid_activities,
            kid_departure et kid_inclusion à `kids`, qui n'en a aucune. Chaque
            tentative repartait en erreur PostgREST, silencieusement. Plus
            aucun lien n'y menait ; l'adresse, elle, pouvait rester dans un
            favori — d'où la redirection plutôt qu'une page absente.
          */}
          <Route path="/kids/edit/:id" element={<RedirectionFicheEnfant />} />
          {/*
            La verification passe par le meme parcours que la creation : cinq
            etapes, ce qui manque en orange dans le champ meme, une seule
            validation a la fin. `/kids/:id` reste la fiche a consulter.
          */}
          <Route path="/kids/:id/verifier" element={<VerifierKidPage />} />
          <Route path="/kids/:id" element={<KidDetailsPage />} />
          <Route path="/authorized-persons" element={<AuthorizedPersonsPage />} />
          <Route path="/authorized-persons/new" element={<NewAuthorizedPersonPage />} />
          <Route path="/authorized-persons/edit/:id" element={<EditAuthorizedPersonPage />} />
          <Route path="/activities" element={<ActivitiesPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/invoice-confirmation" element={<InvoiceConfirmationPage />} />
          <Route path="/registrations" element={<RegistrationsPage />} />
        </Route>
      </Route>

      {/*
        L'administration, en quatre parties (décidé par Éclosion le
        27/09/2026) : Tableau de bord, Comptabilité, Planification — ici — et
        Gestion, qui est `/staff`, plus bas. Les adresses viennent toutes de
        `lib/navigation/chemins.ts`, que les menus et les tests lisent aussi :
        une page n'existe qu'une fois, à une adresse.
      */}
      <Route element={<AdminRoute />}>
        <Route element={<AdminLayout />}>
          <Route path={CHEMINS.tableauDeBord} element={<AdminDashboardPage />} />

          {/* Espace Comptabilité : les écrans existants, à leur adresse canonique. */}
          <Route element={<EspaceLayout espace={COMPTABILITE} />}>
            <Route path={CHEMINS.comptabilite.accueil} element={<ComptabiliteAccueilPage />} />
            {/*
              Le circuit de rapprochement, le seul. L'ancien écran (lecture et
              ECRITURE de `bank_transactions`, gelee depuis le 27 juillet 2025)
              a ete retire le 24 septembre 2026 ; son adresse redirige ici.
            */}
            <Route path={CHEMINS.comptabilite.rapprochement} element={<AdminCodaReconciliationPage />} />
            <Route path={CHEMINS.comptabilite.attente} element={<CompteDAttentePage />} />
            {/*
              L'ecran unique des factures et des notes de credit. Il a remplace
              `/admin/payments` et `/admin/credit-notes`, qui affichaient tous
              deux `invoices.status` — une colonne qui contredit le solde
              calcule sur 57 % des lignes. Les deux adresses redirigent ici.
            */}
            <Route path={CHEMINS.comptabilite.factures} element={<AdminFacturesPage />} />
            <Route path={CHEMINS.comptabilite.attestations} element={<AdminAttestationsPage />} />
            <Route path={CHEMINS.comptabilite.controles} element={<AdminFinancialOverviewPage />} />
            {/*
              La reprise d'ouverture 2026 : le bilan d'ouverture au 01/01/2026,
              prepare, controle et arrete ici (schema `compta`). Rien ne s'y
              comptabilise sans validation de la reprise et confirmation.
            */}
            <Route path={CHEMINS.comptabilite.reprise} element={<AdminRepriseOuverturePage />} />
            {/*
              Les journaux et les ecritures (Lot UI 2) : lecture seule, par les
              vues de `20260928134858_la_lecture_des_journaux`. Aucun geste.
            */}
            <Route path={CHEMINS.comptabilite.journaux} element={<JournauxPage />} />
            <Route path={CHEMINS.comptabilite.ecritures} element={<EcrituresPage />} />
            <Route path={CHEMINS.comptabilite.ecriture} element={<EcriturePage />} />
          </Route>

          {/* Planification : ce que l'administration contenait déjà. */}
          <Route element={<EspaceLayout espace={PLANIFICATION} />}>
            <Route path={CHEMINS.planification.accueil} element={<PlanificationAccueilPage />} />
            <Route path={CHEMINS.planification.centres} element={<AdminCentresPage />} />
            <Route path={CHEMINS.planification.centreNouveau} element={<AdminCentreFichePage />} />
            <Route path={CHEMINS.planification.centreFiche} element={<AdminCentreFichePage />} />
            <Route path={CHEMINS.planification.stages} element={<AdminStagesPage />} />
            <Route path={CHEMINS.planification.programmation} element={<AdminProgrammationPage />} />
            <Route path={CHEMINS.planification.inscriptions} element={<AdminInscriptionsPage />} />
            <Route path={CHEMINS.planification.groupes} element={<AdminRegistrationsByGroupPage />} />
            <Route path={CHEMINS.planification.listeAttente} element={<AdminWaitingListPage />} />
            <Route path={CHEMINS.planification.annulations} element={<AdminCancellationRequestsPage />} />
            <Route path={CHEMINS.planification.inclusion} element={<AdminInclusionRequestsPage />} />
            <Route path={CHEMINS.planification.bonsCadeaux} element={<AdminGiftCodesPage />} />
            <Route path={CHEMINS.planification.ecoles} element={<AdminSchoolsPage />} />
            <Route path={CHEMINS.planification.tarifs} element={<AdminTarifConditionsPage />} />
            <Route path={CHEMINS.planification.newsletter} element={<AdminNewsletterPage />} />
            <Route path={CHEMINS.planification.parametresSite} element={<AdminReglagesSitePage />} />
            {/*
              Les journées pédagogiques (19/10/2026) : la liste et les inscrits,
              la programmation d'une journée, l'école et les classes d'un centre.
            */}
            <Route path={CHEMINS.planification.jpa} element={<AdminJourneesPage />} />
            <Route path={CHEMINS.planification.jpaProgrammer} element={<AdminJourneeFormPage />} />
            <Route path={CHEMINS.planification.jpaModifier} element={<AdminJourneeFormPage />} />
            {/* Les groupes d'une journée, formés par l'équipe (03/11/2026) ; leurs listes s'impriment hors de la mise en page. */}
            <Route path={CHEMINS.planification.jpaGroupes} element={<AdminJourneeGroupesPage />} />
            <Route path={CHEMINS.planification.garderieFacturation} element={<AdminGarderieFacturationPage />} />
            {/*
              Utilisateurs : un seul module (décidé par Éclosion le 27/09/2026),
              rangé dans Planification › Administration. L'accueil porte deux
              tuiles — données de l'utilisateur, données financières —, puis la
              fiche d'un compte et ses deux suites. Demandé le 20 septembre
              2026 : la situation financière d'une famille doit s'atteindre en
              DEUX clics depuis la liste.
            */}
            <Route path={CHEMINS.utilisateurs.accueil} element={<AdminUtilisateursPage />} />
            <Route path={CHEMINS.utilisateurs.donnees} element={<AdminUtilisateursDonneesPage />} />
            <Route path={CHEMINS.utilisateurs.finances} element={<AdminUsersPage />} />
            <Route path={CHEMINS.utilisateurs.fiche} element={<AdminUserPage />} />
            <Route path={CHEMINS.utilisateurs.financesDuCompte} element={<AdminUserFinancesPage />} />
            <Route path={CHEMINS.utilisateurs.edition} element={<AdminUserEditionPage />} />
          </Route>

          {/*
            Les anciennes adresses : elles vivent dans des favoris et des
            courriels. Chacune renvoie en un saut vers l'adresse canonique, en
            gardant `?q=` et l'ancre.
          */}
          {REDIRECTIONS.map((r) => (
            <Route key={r.de} path={r.de} element={<Redirection vers={r.vers} />} />
          ))}
        </Route>
        {/*
          Les listes de présence d'une journée pédagogique, hors de la mise en
          page de l'administration : ce qui s'imprime est la liste, sans menu.
        */}
        <Route path={CHEMINS.planification.jpaListes} element={<AdminJourneeListesPage />} />
      </Route>

      {/* Gestion : l'espace staff "Gestion CDV" (admin, coordi, anim).
          StaffApp gere sa propre navigation interne par etat, d'ou le splat.
          `/espaces`, l'ancien ecran de choix, renvoie chacun vers sa page
          d'arrivee : le tableau de bord general l'a remplace. */}
      <Route element={<StaffRoute />}>
        <Route path="/espaces" element={<RedirectionParRole />} />
        <Route path="/staff/*" element={<StaffApp />} />
      </Route>

      {/*
        L'appel du matin sur le téléphone de l'animateur (24/10/2026) : un
        lien par centre et par semaine, sans compte, hors de toute mise en
        page. Le jeton de l'adresse est le seul droit ; la base le vérifie à
        chaque geste.
      */}
      <Route path="/presences/:jeton/*" element={<PresencesLienPage />} />

      {/*
        Le pointage de la garderie (31/10/2026) : un lien par école et par
        année scolaire, sans compte, hors de toute mise en page. Même règle :
        le jeton est le seul droit, la base le vérifie à chaque geste.
      */}
      <Route path="/garderie/:jeton" element={<PointageLienPage />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;